import React, { useState, useContext, useEffect } from "react";
import { TextField, Button, Grid, Box, Typography } from "@mui/material";
import SessionStorageService from "../../../services/StorageService";
import { ConstantAPISContext } from "../../../context/ConstantAPISContext";
import DuyurularAPI from "../../../apis/DuyurularAPI";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { showToast } from "../../../helpers/ToastifyHelper";

// Duyuru veri modeli tipi
interface DuyuruModel {
  sqId?: number;
  sira: number;
  icerik: string;
  kaydeden: string;
  yayinlanmaBaslangicTarihi: string | null;
  yayinlanmaBitisTarihi: string | null;
  kayitTarihi?: string | null;
  guncelleyen?: string | null;
  guncellemeTarihi?: string | null;
}

const getEmptyDuyuru = (): DuyuruModel => {
  const user = SessionStorageService.getUserInfo();
  return {
    sira: 1,
    icerik: "",
    kaydeden: user && user.sqId ? user.sqId.toString() : "",
    yayinlanmaBaslangicTarihi: null,
    yayinlanmaBitisTarihi: null,
  };
};

const DuyuruSchema = Yup.object().shape({
  icerik: Yup.string().required("İçerik zorunludur"),
  sira: Yup.number().required("Sıra zorunludur").min(1, "Sıra 1 veya daha büyük olmalı"),
  yayinlanmaBaslangicTarihi: Yup.string()
    .nullable()
    .required("Başlangıç tarihi zorunludur"),
});

const Duyurular: React.FC = () => {
  const { duyurularListe } = useContext(ConstantAPISContext);
  const [duyurular, setDuyurular] = useState<DuyuruModel[]>([]);
  const [eklemeModu, setEklemeModu] = useState(false);
  const [guncelleModu, setGuncelleModu] = useState<number | null>(null); // index

  useEffect(() => {
    setDuyurular(duyurularListe || []);
  }, [duyurularListe]);

  // Satır güncelleme (Formik ile)
  const handleUpdate = async (values: DuyuruModel, idx: number) => {
    try { 
      const user = SessionStorageService.getUserInfo();
      const kaydeden = user && user.sqId ? user.sqId.toString() : "";
      const payload: DuyuruModel = {
        sqId: values.sqId ?? undefined,
        sira: values.sira,
        icerik: values.icerik,
        kaydeden: kaydeden,
        yayinlanmaBaslangicTarihi: values.yayinlanmaBaslangicTarihi,
        yayinlanmaBitisTarihi: values.yayinlanmaBitisTarihi,
      }; 
      const response = await DuyurularAPI.saveDuyuru(payload);
      if (response.success && response.data && response.data.sonuc === 0) {
        setDuyurular((prev) => prev.map((d, i) => (i === idx ? { ...values, sqId: response.data.sqId, kaydeden: kaydeden } : d)));
        setGuncelleModu(null);
        showToast("Güncelleme başarılı", "success");
      } else {
        showToast(response.data?.sonucAciklamasi || "Güncelleme başarısız", "error");
      }
    } catch (e) {
      showToast("Güncelleme başarısız", "error");
    }
  };

  // Yeni satır ekle
  const handleAdd = () => {
    setEklemeModu(true);
  };

  // Eklemeyi tamamla (Formik ile)
  const handleBatchInsert = async (values: DuyuruModel, resetForm: () => void) => {
    try {
      const user = SessionStorageService.getUserInfo();
      const kaydeden = user && user.sqId ? user.sqId.toString() : "";
      const payload: DuyuruModel = {
        sqId: undefined,
        sira: values.sira,
        icerik: values.icerik,
        kaydeden: kaydeden,
        yayinlanmaBaslangicTarihi: values.yayinlanmaBaslangicTarihi,
        yayinlanmaBitisTarihi: values.yayinlanmaBitisTarihi,
      };
      const response = await DuyurularAPI.saveDuyuru(payload);
      if (response.success && response.data && response.data.sonuc === 0) {
        setDuyurular((prev) => [...prev, { ...values, sqId: response.data.sqId, kaydeden: kaydeden }]);
        setEklemeModu(false);
        resetForm();
        showToast("Ekleme başarılı", "success");
      } else {
        showToast(response.data?.sonucAciklamasi || "Ekleme başarısız", "error");
      }
    } catch (e) {
      showToast("Ekleme başarısız", "error");
    }
  };

  // Eklemeyi iptal et
  const handleCancel = () => {
    setEklemeModu(false);
  };

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        Duyurular
      </Typography>
      {!eklemeModu && (
        <Button variant="contained" onClick={handleAdd} sx={{ mb: 2 }}>
          Ekle
        </Button>
      )}
      <Grid container spacing={2}>
        {duyurular.map((duyuru, idx) =>
          guncelleModu === idx ? (
            <Grid item xs={12} key={idx}>
              <Formik
                initialValues={duyuru}
                validationSchema={DuyuruSchema}
                onSubmit={async (values) => {
                  await handleUpdate(values, idx);
                }}
              >
                {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
                  <Form>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={2}>
                        <TextField
                          size="small"
                          label="Sıra"
                          type="number"
                          name="sira"
                          value={values.sira}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.sira && Boolean(errors.sira)}
                          helperText={touched.sira && errors.sira}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          size="small"
                          label="İçerik"
                          name="icerik"
                          value={values.icerik}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.icerik && Boolean(errors.icerik)}
                          helperText={touched.icerik && errors.icerik}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          size="small"
                          type="date"
                          label="Başlangıç Tarihi"
                          name="yayinlanmaBaslangicTarihi"
                          value={values.yayinlanmaBaslangicTarihi ? values.yayinlanmaBaslangicTarihi.substring(0, 10) : ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.yayinlanmaBaslangicTarihi && Boolean(errors.yayinlanmaBaslangicTarihi)}
                          helperText={touched.yayinlanmaBaslangicTarihi && errors.yayinlanmaBaslangicTarihi}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          size="small"
                          type="date"
                          label="Bitiş Tarihi"
                          name="yayinlanmaBitisTarihi"
                          value={values.yayinlanmaBitisTarihi ? values.yayinlanmaBitisTarihi.substring(0, 10) : ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.yayinlanmaBitisTarihi && Boolean(errors.yayinlanmaBitisTarihi)}
                          helperText={touched.yayinlanmaBitisTarihi && errors.yayinlanmaBitisTarihi}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Button variant="contained" color="success" type="submit">
                          Kaydet
                        </Button>
                        <Button variant="outlined" color="error" onClick={() => setGuncelleModu(null)} sx={{ ml: 1 }}>
                          Vazgeç
                        </Button>
                      </Grid>
                    </Grid>
                  </Form>
                )}
              </Formik>
            </Grid>
          ) : (
            <React.Fragment key={idx}>
              <Grid item xs={12} md={2}>
                <TextField
                  size="small"
                  label="Sıra"
                  type="number"
                  value={duyuru.sira}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  size="small"
                  label="İçerik"
                  value={duyuru.icerik}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  size="small"
                  type="date"
                  label="Başlangıç Tarihi"
                  value={duyuru.yayinlanmaBaslangicTarihi ? duyuru.yayinlanmaBaslangicTarihi.substring(0, 10) : ""}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  size="small"
                  type="date"
                  label="Bitiş Tarihi"
                  value={duyuru.yayinlanmaBitisTarihi ? duyuru.yayinlanmaBitisTarihi.substring(0, 10) : ""}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button variant="outlined" onClick={() => setGuncelleModu(idx)}>
                  Güncelle
                </Button>
              </Grid>
            </React.Fragment>
          )
        )} 
        {eklemeModu && (
          <Grid item xs={12}>
            <Formik
              initialValues={{ ...getEmptyDuyuru(), sira: duyurular.length + 1 }}
              validationSchema={DuyuruSchema}
              onSubmit={async (values, { resetForm }) => {
                await handleBatchInsert(values, resetForm);
              }}
            >
              {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
                <Form>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={2}>
                      <TextField
                        size="small"
                        label="Sıra"
                        type="number"
                        name="sira"
                        value={values.sira}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.sira && Boolean(errors.sira)}
                        helperText={touched.sira && errors.sira}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        size="small"
                        label="İçerik"
                        name="icerik"
                        value={values.icerik}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.icerik && Boolean(errors.icerik)}
                        helperText={touched.icerik && errors.icerik}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        size="small"
                        type="date"
                        label="Başlangıç Tarihi"
                        name="yayinlanmaBaslangicTarihi"
                        value={values.yayinlanmaBaslangicTarihi ? values.yayinlanmaBaslangicTarihi.substring(0, 10) : ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.yayinlanmaBaslangicTarihi && Boolean(errors.yayinlanmaBaslangicTarihi)}
                        helperText={touched.yayinlanmaBaslangicTarihi && errors.yayinlanmaBaslangicTarihi}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        size="small"
                        type="date"
                        label="Bitiş Tarihi"
                        name="yayinlanmaBitisTarihi"
                        value={values.yayinlanmaBitisTarihi ? values.yayinlanmaBitisTarihi.substring(0, 10) : ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.yayinlanmaBitisTarihi && Boolean(errors.yayinlanmaBitisTarihi)}
                        helperText={touched.yayinlanmaBitisTarihi && errors.yayinlanmaBitisTarihi}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                          <Button
                            variant="contained"
                            color="success"
                            type="submit"
                            fullWidth
                          >
                            Tamamla
                          </Button>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={handleCancel}
                            fullWidth
                          >
                            Vazgeç
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default Duyurular;
